import clsx from "clsx";
import { chunk } from "lodash";

import {
  Instruction,
  Matrix,
  MatrixFunction,
  Name
} from "../../../@types/visualization";

import { defineHeatmapStyles, defineRegionStyles } from "../utils";

import { SVG_PARAMS } from "../consts";

type Props = {
  matrix?: Matrix;
  previewType: string;
  functions: Array<MatrixFunction>;
  onMouseEnter: (
    instructions: Array<Instruction[]> | null,
    names: Array<Name>,
    event: React.MouseEvent
  ) => void;
  height: number;
};

const getItems = (functions: Array<MatrixFunction>) =>
  functions.reduce<Record<string, MatrixFunction>>((acc, curr) => {
    // eslint-disable-next-line no-param-reassign
    acc[curr.block] = curr;
    return acc;
  }, {});

const SVG_CHUNK_SIZE = 100;

const SvgRectangleList = ({
  height,
  matrix,
  functions,
  previewType,
  onMouseEnter
}: Props) => {
  const items = getItems(functions);

  const styleRegion = defineRegionStyles(matrix?.styles?.region ?? []);
  const styleEntropy = defineHeatmapStyles(matrix?.styles?.heatmap ?? []);

  // Flatten all blocks and create JSX directly (eliminates intermediate array)
  const allBlocks = [...(matrix?.blocks || []), ...(matrix?.extras || [])];

  // Create rectangles directly with cumulative index calculation
  let cumulativeIndex = 0;
  const rectangles = allBlocks.flatMap(block => {
    const entries = parseInt(block.entries, 10);
    const startIndex = cumulativeIndex;
    cumulativeIndex += entries;

    return Array.from({ length: entries }, (_, entryIndex) => {
      const index = startIndex + entryIndex;
      const x = index % SVG_PARAMS.pColumns;
      const y = Math.floor(index / SVG_PARAMS.pColumns);

      const machineClassName =
        previewType === "structure"
          ? styleRegion[block.region]?.machine
          : styleEntropy[block.entropy]?.machine;

      const instructions = items?.[index]?.instructions ?? null;
      const names = items?.[index]?.names ?? null;
      const functionClassName = names?.length ? `p-${names[0].type}` : null;

      return (
        <rect
          width="7"
          height="7"
          className={clsx(machineClassName, functionClassName)}
          x={x * 8}
          y={y * 8}
          key={index}
          data-key={names && index}
          onMouseEnter={event => {
            if (!names) return;
            onMouseEnter(instructions, names, event);
          }}
        >
          <title>{styleRegion[block.region]?.name}</title>
        </rect>
      );
    });
  });

  return (
    <svg className="file-visualization__blocks" width="512" height={height}>
      {chunk(rectangles, SVG_CHUNK_SIZE)}
    </svg>
  );
};

export default SvgRectangleList;
